import { useQuery } from '@tanstack/react-query';
import {
  AccessorKeyColumnDef,
  createColumnHelper,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable
} from '@tanstack/react-table';
import FeatherIcon from 'feather-icons-react';
import { useEffect, useState } from 'react';
import { Card, Col, Container, Form, InputGroup, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap';
import { Route, Routes } from 'react-router-dom';

import { getInvoices } from '../../apis/queries';
import EmptyTableState from '../../components/EmptyState/EmptyStateTable';
import Loader from '../../components/Loader';
import PaginationControls from '../../components/Pagination/PaginationControls';
import { currencyFormatter } from '../../helpers/currencyFormatter';
import { Invoice } from '../../types';
import { formatDateShort } from '../../utils/formatShortDate';
import { formatTime } from '../../utils/formatTime';
import AnalyticsHeader from '../../widgets/Header';

type INVOICE_PAYMENT_MODE = 'cash' | 'transfer';

interface InvoiceTableProps {
  query?: Record<'modeOfPayment', INVOICE_PAYMENT_MODE>;
}

export const INVOICES_QUERY_KEY = ['invoices'];

const globalFilterFn: FilterFn<Invoice> = (row, columnId, filterValue) => {
  const invoiceNumber = row.original.invoiceNumber?.toString().toLowerCase();
  const orderNumber = row.original.orderNumber?.toString().toLowerCase();
  const customerName = row.original.customer?.name?.toLowerCase() ?? '';
  const createdBy = row.original.createdBy?.fullName?.toLowerCase() ?? '';
  const searchValue = filterValue.toLowerCase();

  return (
    invoiceNumber.includes(searchValue) ||
    orderNumber.includes(searchValue) ||
    customerName.includes(searchValue) ||
    createdBy.includes(searchValue)
  );
};

const columnHelper = createColumnHelper<Invoice>();

const columns = [
  columnHelper.accessor('invoiceNumber', {
    header: 'Invoice Number',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('orderNumber', {
    header: 'Order Number',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('customer.name', {
    header: 'Customer',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('modeOfPayment', {
    header: 'Payment Mode',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('amount', {
    header: 'Amount',
    cell: info => currencyFormatter(info.getValue()),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created At',
    cell: info => formatDateShort(info.getValue()),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created Time',
    id: 'createdTime',
    cell: info => formatTime(info.getValue()),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('createdBy.fullName', {
    header: 'Created By',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('comments', {
    header: 'Comments',
    cell: info => {
      const comment = info.getValue() ?? '';
      const truncatedComment = comment?.length > 25 ? `${comment.slice(0, 25)}...` : comment;

      return (
        <OverlayTrigger placement="top" overlay={<Tooltip>{comment}</Tooltip>}>
          <span>{truncatedComment}</span>
        </OverlayTrigger>
      );
    },
    footer: info => info.column.id ?? ''
  })
];

const limit = 100;

const InvoicesTable = ({ query }: InvoiceTableProps) => {
  const [page, setPage] = useState(1);
  const compositeQueryKey = query
    ? [...INVOICES_QUERY_KEY, { ...query, page, limit }]
    : [INVOICES_QUERY_KEY, { page, limit }];
  const { data, isLoading } = useQuery({
    queryKey: compositeQueryKey,
    queryFn: () => getInvoices({ ...query, page, limit })
  });
  const invoices = data?.results ?? [];

  useEffect(() => {
    setPage(1);
  }, [query]);

  const { count } = data ?? {};

  const nextPage = data?.nextPage;
  const prevPage = data?.prevPage;

  let noInvoicesMessage;

  switch (query?.modeOfPayment) {
    case 'cash':
      noInvoicesMessage = invoices && invoices.length === 0 && (
        <EmptyTableState message="No cash mode of payment yet." />
      );
      break;
    case 'transfer':
      noInvoicesMessage = invoices && invoices.length === 0 && (
        <EmptyTableState message="No transfer mode of payment yet." />
      );
      break;
    default:
      noInvoicesMessage = null;
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <>
      <AnalyticsHeader title="Invoices" tabs={tabRoutes} />
      <div className="main-content">
        <Container fluid>
          <Row className="justify-content-center">
            <Col xs={12}>
              {isLoading ? (
                <Loader />
              ) : noInvoicesMessage ? (
                noInvoicesMessage
              ) : invoices && invoices.length > 0 ? (
                <>
                  <TableList invoices={invoices} columns={columns} />
                  <PaginationControls
                    currentPage={page}
                    totalPages={count ?? 0}
                    onPageChange={handlePageChange}
                    nextPage={nextPage ?? null}
                    prevPage={prevPage ?? null}
                  />
                </>
              ) : (
                <EmptyTableState message="No invoices yet." />
              )}
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

const tabRoutes = [
  {
    title: 'All',
    path: 'all',
    tabLink: '/invoices/all',
    element: <InvoicesTable />
  },
  {
    title: 'Cash',
    path: 'cash',
    tabLink: '/invoices/cash',
    element: <InvoicesTable query={{ modeOfPayment: 'cash' }} />
  },
  {
    title: 'Transfer',
    path: 'transfer',
    tabLink: '/invoices/transfer',
    element: <InvoicesTable query={{ modeOfPayment: 'transfer' }} />
  }
];

const Invoices = () => {
  return (
    <div className="main-content">
      <Routes>
        {tabRoutes.map(({ path, element, title }) => (
          <Route key={title} path={path} element={element} />
        ))}
      </Routes>
    </div>
  );
};

export default Invoices;

interface TableListProps {
  invoices: Invoice[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: AccessorKeyColumnDef<Invoice, any>[];
}

const TableList = ({ invoices, columns }: TableListProps) => {
  const [globalFilter, setGlobalFilter] = useState<string | undefined>(undefined);

  const tableInstance = useReactTable({
    data: invoices!,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: { globalFilter },
    globalFilterFn,
    onGlobalFilterChange: setGlobalFilter
  });

  return (
    <Card>
      <Card.Header>
        <InputGroup className="input-group-merge input-group-flush input-group-reverse">
          <Form.Control
            type="search"
            placeholder="Search"
            onChange={e => setGlobalFilter(e.target.value ? e.target.value : undefined)}
          />
          <InputGroup.Text>
            <FeatherIcon icon="search" size="1em" />
          </InputGroup.Text>
        </InputGroup>
      </Card.Header>
      <div
        style={{
          overflowX: 'auto',
          maxHeight: 'calc(100vh - 300px)',
          position: 'relative'
        }}
      >
        <Table size="sm" className="card-table table-nowrap" hover>
          <thead className="position-sticky top-0 z-2">
            {tableInstance.getHeaderGroups().map(headerGroup => {
              return (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => {
                    return (
                      <th colSpan={header.colSpan} key={header.id}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody>
            {tableInstance.getRowModel().rows.map(row => {
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => {
                    return (
                      <td key={cell.id}>
                        {/* <Link to={`/invoice/${row.original._id}`}> */}
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        {/* </Link> */}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </Card>
  );
};
